// @flow strict
import * as React from 'react'
import _get from 'lodash.get'
import { Link } from 'gatsby'
import { Card, ResponsiveImage } from '@mondough/public-web-components'
import { renderDate } from '@mondough/public-web-components'
import {
  buildDefaultImage,
  buildImageSourceSets,
} from '../../utils/buildPostResponsiveImages'

import styles from './BlogCard.module.scss'

import BlogImagePlaceholder from '../../static/blog-placeholder@2x.png'

export type Props<T> = {|
  post: T,
  slugPrefixForATag?: string,
|}

const extractPostDetails = post => ({
  id: _get(post, 'id', ''),
  title: _get(post, 'title', ''),
  slug: _get(post, 'webSlug', ''),
  excerpt: _get(post, 'description.text', ''),
  imageAlt: _get(post, 'ogImage.description', ''),
  imageUrl: _get(post, 'ogImage.file.url', ''),
  ogImageUrl: _get(post, 'ogImageUrl', ''),
  category: _get(post, 'category.[0].categoryName', ''),
  startDate: _get(post, 'startDate', ''),
})

const BlogCard = <T>({ post, slugPrefixForATag }: Props<T>) => {
  const {
    title,
    startDate,
    slug,
    excerpt,
    imageAlt,
    imageUrl,
    ogImageUrl,
    category,
  } = extractPostDetails(post)

  const desktopImageSize = 674

  const deviceWidthsToImageWidths: { [string]: number } = {
    '<320': 288,
    '<360': 328,
    '<375': 343,
    '<393': 361,
    '<414': 382,
    '<768': 735,
    '>767': desktopImageSize,
  }
  const imageWidths = Object.values(deviceWidthsToImageWidths)
  const src = imageUrl || ogImageUrl || BlogImagePlaceholder
  const sizes = Object.entries(deviceWidthsToImageWidths)
    .map(([deviceWidthAndMedia, imageWidth]) => {
      const mediaQuery = deviceWidthAndMedia.charAt(0) === '>' ? 'min' : 'max'
      const deviceWidth = deviceWidthAndMedia.slice(1)
      return `(${mediaQuery}-width: ${deviceWidth}px) ${Number(imageWidth)}px`
    })
    .join(', ')
  const sources = {
    srcSet: buildImageSourceSets(imageWidths, src),
    sizes,
  }
  const defaultImage = buildDefaultImage(imageUrl, desktopImageSize * 2)
  return (
    <Card
      as={
        slugPrefixForATag != null ? (
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            href={slugPrefixForATag + slug}
            rel="noopener noreferrer"
            target="_blank"
          />
        ) : (
          <Link to={slug} />
        )
      }
    >
      <Card.Media className={styles.image}>
        <ResponsiveImage
          alt={imageAlt}
          defaultImage={defaultImage}
          sources={[sources]}
        />
      </Card.Media>
      <Card.Content>
        <Card.ContentHeader>
          {category && <Card.Label>{category}</Card.Label>}
          <Card.Metadata
            as={<time dateTime={renderDate(startDate, 'YYYY-MM-DD')} />}
          >
            {renderDate(startDate, 'DD-MONTH-YYYY')}
          </Card.Metadata>
        </Card.ContentHeader>
        {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
        <Card.Heading as={<h2 />}>{title || ''}</Card.Heading>
        <Card.Text>{excerpt}</Card.Text>
      </Card.Content>
    </Card>
  )
}

export default BlogCard
